import type { ReactNode } from 'react';
import styles from '~/app/components/asset-detail/AssetLogsTable/AssetLogsTable.module.css';

function charAt(value: string, index: number): string {
  return value.charAt(index);
}

function isDigit(char: string): boolean {
  return char >= '0' && char <= '9';
}

function isIdentContinue(char: string): boolean {
  return (
    (char >= 'A' && char <= 'Z') ||
    (char >= 'a' && char <= 'z') ||
    (char >= '0' && char <= '9') ||
    char === '_'
  );
}

function readJsonString(
  line: string,
  start: number,
): { end: number; value: string } | null {
  if (charAt(line, start) !== '"') {
    return null;
  }

  let index = start + 1;
  while (index < line.length) {
    const char = charAt(line, index);
    if (char === '\\') {
      index += 2;
      continue;
    }
    if (char === '"') {
      return {
        end: index + 1,
        value: line.slice(start, index + 1),
      };
    }
    index += 1;
  }

  return null;
}

function readNumber(
  line: string,
  start: number,
): { end: number; value: string } | null {
  let index = start;
  if (charAt(line, index) === '-') {
    index += 1;
  }
  if (!isDigit(charAt(line, index))) {
    return null;
  }
  while (isDigit(charAt(line, index))) {
    index += 1;
  }
  if (charAt(line, index) === '.' && isDigit(charAt(line, index + 1))) {
    index += 1;
    while (isDigit(charAt(line, index))) {
      index += 1;
    }
  }
  const exp = charAt(line, index);
  if (exp === 'e' || exp === 'E') {
    let expIndex = index + 1;
    const sign = charAt(line, expIndex);
    if (sign === '+' || sign === '-') {
      expIndex += 1;
    }
    if (!isDigit(charAt(line, expIndex))) {
      return {
        end: index,
        value: line.slice(start, index),
      };
    }
    index = expIndex;
    while (isDigit(charAt(line, index))) {
      index += 1;
    }
  }

  return {
    end: index,
    value: line.slice(start, index),
  };
}

function readLiteral(
  line: string,
  start: number,
): { end: number; value: string } | null {
  for (const literal of ['true', 'false', 'null'] as const) {
    if (!line.startsWith(literal, start)) {
      continue;
    }
    const next = charAt(line, start + literal.length);
    if (!isIdentContinue(next)) {
      return {
        end: start + literal.length,
        value: literal,
      };
    }
  }
  return null;
}

export function highlightJsonLine(line: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let index = 0;
  let key = 0;

  while (index < line.length) {
    const stringToken = readJsonString(line, index);
    if (stringToken) {
      let cursor = stringToken.end;
      while (cursor < line.length && /\s/.test(charAt(line, cursor))) {
        cursor += 1;
      }
      if (charAt(line, cursor) === ':') {
        nodes.push(
          <span key={`k-${key++}`} className={styles.jsonKey}>
            {stringToken.value}
          </span>,
        );
        if (cursor > stringToken.end) {
          nodes.push(
            <span key={`ws-${key++}`} className={styles.jsonPunct}>
              {line.slice(stringToken.end, cursor)}
            </span>,
          );
        }
        nodes.push(
          <span key={`c-${key++}`} className={styles.jsonPunct}>
            :
          </span>,
        );
        index = cursor + 1;
        continue;
      }

      nodes.push(
        <span key={`s-${key++}`} className={styles.jsonString}>
          {stringToken.value}
        </span>,
      );
      index = stringToken.end;
      continue;
    }

    const numberToken = readNumber(line, index);
    if (numberToken) {
      nodes.push(
        <span key={`n-${key++}`} className={styles.jsonNumber}>
          {numberToken.value}
        </span>,
      );
      index = numberToken.end;
      continue;
    }

    const literalToken = readLiteral(line, index);
    if (literalToken) {
      nodes.push(
        <span key={`l-${key++}`} className={styles.jsonLiteral}>
          {literalToken.value}
        </span>,
      );
      index = literalToken.end;
      continue;
    }

    nodes.push(
      <span key={`p-${key++}`} className={styles.jsonPunct}>
        {charAt(line, index)}
      </span>,
    );
    index += 1;
  }

  return nodes;
}
